import * as Sentry from '@sentry/vue';
import { useStorage } from '@vueuse/core';
import { useGlobalAbortController } from '@Composables';
import { APP_URL } from '@Constants';
import {
  isOnPendingAuthRoute,
  recoverPendingAuthRoute,
} from '../../modules/System/Auth/composables/recoverPendingAuthRoute';

// NOTE :: Frontend "don't-report" list — the mirror of Laravel's $internalDontReport.
// The backend never sends these expected exceptions to its error tracker, and neither
// should we: they are handled, user-facing conditions (auth redirect, authorization,
// validation, not-found) or client connectivity blips — not actionable app errors.
const EXPECTED_ERROR_STATUSES = [401, 403, 404, 409, 422, 428];
const EXPECTED_TRANSPORT_CODES = ['ERR_NETWORK', 'ECONNABORTED'];

async function errorHandler(error: any) {
  const abortedSignalError = error.code === 'ERR_CANCELED';
  if (abortedSignalError) {
    // NOTE :: Return a never-settling promise to silently discard aborted requests.
    // This prevents the rejection from reaching component .catch() blocks (which would show toast notifications).
    // Since aborted requests only happen during route navigation, the calling components are being unmounted anyway.
    return new Promise(() => {});
  }

  const status = error.response?.status;

  // NOTE :: Keyed off the api's marker, never a bare 404 — other calls 404 for their own reasons.
  const isForeignShelterRecord = status === 404 && error.response?.data?.reason === 'not_in_shelter';
  if (isForeignShelterRecord) {
    // Cancel first: a hard navigation truncates in-flight responses and the dying page throws.
    useGlobalAbortController().cancelAllRequests();

    // replace, not href: the bad id must not stay in history, or Back returns to it and bounces forward again.
    window.location.replace('/shelter/not-found');

    return new Promise(() => {});
  }

  const isAuthError = [401, 403].includes(status);
  const isAuthPrecondition = status === 428;
  const isCartConflict = status === 409;

  // NOTE :: Loaded on demand, not imported at the top. A static import would run
  // @ShoppingCartModule -> @Libraries -> router -> routes -> back into @ShoppingCartModule, and that
  // cycle leaves the module half-evaluated at load time — the enums it needs are still undefined.
  // Only a 409 that carries a code pays for the import, and by then everything is initialized.
  const hasCartErrorCode = isCartConflict && Boolean(error.response?.data?.code);

  if (hasCartErrorCode) {
    const { CART_SESSION_ERROR, useCartSessionRecovery } = await import('@ShoppingCartModule');
    const isCartSessionError = Object.values(CART_SESSION_ERROR).includes(error.response.data.code);

    if (isCartSessionError) {
      useCartSessionRecovery().onCartSessionError(error.response.data);

      // NOTE :: Never-settling promise so the caller's .catch() does not also toast — recovery has
      // already moved the user and told them why.
      return new Promise(() => {});
    }
  }

  const isExpectedError = EXPECTED_ERROR_STATUSES.includes(status) || EXPECTED_TRANSPORT_CODES.includes(error.code);

  if (isAuthPrecondition) {
    const pendingRoute = recoverPendingAuthRoute(error);
    if (pendingRoute && !isOnPendingAuthRoute(window.location.pathname, pendingRoute)) {
      window.location.href = pendingRoute;

      return new Promise(() => {});
    }
  }

  const structuredError = {
    code: error.code,
    data: error.response?.data,
    message: error.message,
    method: error.config?.method,
    status: error.response?.status,
    url: error.config?.url,
  };

  console.error('API Error', structuredError);

  if (!isExpectedError) {
    Sentry.captureException(error, {
      tags: {
        type: 'axios',
        section: 'API Error Handler',
      },
      extra: structuredError,
    });
  }

  if (isAuthError) {
    // NOTE :: Written via raw useStorage (not the session store) on purpose — this
    // interceptor can run in a non-Vue context where Pinia may not be active. The
    // store's isInvalidSession (also useStorage on the same key) syncs from this write.
    const STORE_BASE_NAME = 'af-session-store';
    const getKey = (key: string) => `${STORE_BASE_NAME}__${key}`;
    const storeKeys = {
      isInvalidSession: getKey('isInvalidSession'),
    };

    const isInvalidSession = useStorage(storeKeys.isInvalidSession, true);

    isInvalidSession.value = true;
    // NOTE :: We set this lock in case there are multiple tabs open.
    // If another tab is already logging out, we wait for it to finish.
    // This prevents CORS issues when logging out from multiple tabs.
    sessionStorage.setItem('af-logout-lock', 'true');

    const loginPath = `${APP_URL}/login`;
    const isLoginRoute = window.location.href === loginPath;
    if (isLoginRoute) {
      // Already on login page, still reject so callers handle the error properly
      throw error;
    }

    const currentUrl = new URL(window.location.href);
    const currentPath = `${currentUrl.hostname}${currentUrl.pathname}`;
    const isNotLoginRoute = currentPath !== loginPath;

    if (isNotLoginRoute) window.location.href = '/login';
  }

  throw error;
}

export default errorHandler;
